import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Spinner, Card, Button, Callout, Intent, Text } from '@blueprintjs/core';
import { githubApi } from '../../api/github';

const GithubIntegrationLanding: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Memoize params and derived values to stabilize useEffect dependencies
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const installed = useMemo(() => params.get('installed') === '1', [params]);
  const installationId = useMemo(() => params.get('installation_id'), [params]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    console.log(
      '[GithubIntegrationLanding] useEffect triggered.',
      {
        pathname: location.pathname,
        search: location.search,
        // rawParams: params.toString(), // params is already memoized
        derivedInstalledState: installed,
        derivedInstallationIdState: installationId,
        loadingState: loading,
        confirmedState: confirmed,
        errorState: error,
      }
    );

    if (installed && installationId) {
      // Only attempt connection check if not already loading, confirmed, or in an error state
      // for the current set of parameters.
      if (!loading && !confirmed && !error) {
        setLoading(true);
        // setError(null); // setError(null) was here, but could cause a loop if error was just set. Let specific conditions set/clear error.

        githubApi.isConnected()
          .then(response => {
            if (response && response.connected) {
              setConfirmed(true);
              timeoutId = setTimeout(() => {
                // Get org slug from auth or default to login
                const orgSlug = localStorage.getItem('orgSlug');
                navigate(orgSlug ? `/${orgSlug}/mindmap` : '/login');
              }, 2000);
            } else {
              setError('Backend confirmation failed. The GitHub App might not be fully connected yet. Please try refreshing or check the integration settings.');
            }
          })
          .catch(err => {
            console.error("Error confirming GitHub connection:", err);
            setError(`Failed to confirm GitHub connection: ${err.message || 'Unknown error'}. Please try again or check connection settings.`);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } else if (installed && !installationId) {
        if (!error) { // Set error only if not already in an error state to prevent loops
            setError('GitHub installation ID is missing. Cannot confirm connection.');
        }
    } else {
      if (installationId) { // This implies installed=false but installationId is present
        if (!error) { // Set error only if not already in an error state
            setError('GitHub connection process was not completed successfully (installed flag not set).');
        }
      }
      // If neither installed nor installationId, it implies direct navigation or a non-install flow.
      // The renderContent function handles this by showing a generic message.
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
    // location.pathname is added because if the path changes, we might need to re-evaluate.
    // Other states (loading, confirmed, error) are critical to prevent re-triggering the API call logic.
  }, [navigate, installed, installationId, loading, confirmed, error, location.pathname]);

  const renderContent = () => {
    if (loading) {
      return (
        <>
          <Spinner size={30} />
          <Text style={{ marginTop: 16 }}>Confirming GitHub connection...</Text>
        </>
      );
    }

    if (error) {
      return (
        <>
          <Callout intent={Intent.DANGER} icon="error" title="Connection Problem" style={{ marginBottom: 16 }}>
            {error}
          </Callout>
          <Button intent="primary" onClick={() => navigate('/integrations')}>Back to Integrations</Button>
        </>
      );
    }

    if (confirmed) {
      return (
        <>
          <Callout intent={Intent.SUCCESS} icon="tick" title="Success!" style={{ marginBottom: 16 }}>
            GitHub successfully connected and confirmed! Redirecting you shortly...
          </Callout>
          <Button intent="primary" onClick={() => navigate('/mindmap')}>Continue to Mindmap</Button>
        </>
      );
    }

    // Default state: if 'installed' is true but not yet confirmed or failed (should be covered by loading/error)
    // Or if 'installed' is false (meaning connection failed before reaching here or page accessed directly)
    if (installed) {
        // This case should ideally be handled by loading or error states.
        // If we reach here, it's an unexpected state.
        return (
             <>
                <Spinner size={30} />
                <Text style={{ marginTop: 16 }}>Processing GitHub connection...</Text>
             </>
        );
    }

    // General failure or direct access without 'installed' param
    return (
        <>
            <Callout intent={Intent.WARNING} icon="warning-sign" title="GitHub Connection" style={{ marginBottom: 16 }}>
                It seems the GitHub connection process was not completed or you've landed here directly.
                Please try initiating the connection from the integrations page.
            </Callout>
            <Button intent="primary" onClick={() => navigate('/integrations')}>Go to Integrations</Button>
        </>
    );
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: 20 }}>
      <Card elevation={2} style={{ maxWidth: 520, padding: 30, textAlign: 'center' }}>
        {renderContent()}
      </Card>
    </div>
  );
};

export default GithubIntegrationLanding; 